import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Table, Row, Tabs, Select, Button
} from 'antd';
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import LeafDiagnostics from './index';

import './style.less';
import moment from 'moment';
import { calculatorsGroup, curPoints, getCurrentCalculatorMordovia } from '../../../../models/mordovia/selectors';
import { getProtocol, clearProtocol } from '../../../../models/fed-registry/actions';
import { curProtocol } from '../../../../models/fed-registry/selectors';
import { toFixed } from '../../../../utils/utils';
import { removeCalculate } from '../../../../models/item-calculate/actions';
import { calculateDiagnostics } from '../../../../models/leaf-diagnostics/actions';
import { getCalculatorMordovia } from '../../../../models/mordovia/actions';

const elementsArray = [
    'k2', 'n', 'p', 'ks', 'kcl', 'k3', 'ca', 'mg', 'b',
    'cu', 'k4', 'zn', 'mn', 'fe', 'mo', 'k5', 'co', 'i', 'k6'
];

const { TabPane } = Tabs;

const LeafInterpetation = ({

}) => {
    const { t } = useTranslation('leaf diagnostics');
    const { t: tTable } = useTranslation('fed registry');
    const match = useRouteMatch();
    const dispatch = useDispatch();

    const calcGroup = useSelector(state => calculatorsGroup(state));
    const currentCalculator = useSelector(state => getCurrentCalculatorMordovia(state));
    const currentFedProtocol = useSelector(state => curProtocol(state));

    const [calculatorsList, setCalculatorsList] = useState([]);
    const [currentSelectedCalculator, setCurrentSelectedCalculator] = useState({});

    const [selectedDate, setSelectedDate] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedZone, setSelectedZone] = useState([]);
    const [selectedPoint, setSelectedPoint] = useState([]);
    const [fedPoints, setFedPoints] = useState([]);

    useEffect(() => {
        const calcYear = currentCalculator?.year_group?.years?.filter(yearObj => yearObj?.year?.toString() === match.params.year?.toString());
        if (calcYear?.[0]) {
            const calcList = calcYear?.[0]?.calculator_group?.calculators?.filter(calc => calc?.calculator_type === 12);
            setCalculatorsList(calcList);
        }
    }, [currentCalculator]);

    useEffect(() => {
        const pointsArray = [];
        currentCalculator?.year_group?.years
            ?.filter(yearObj => yearObj?.year?.toString() === match.params.year?.toString())
            ?.forEach(yearObj => {
                yearObj?.fed_points?.forEach(fedPoint => {
                    pointsArray.push({
                        id: fedPoint?.id,
                        point_number: fedPoint?.point_number,
                        date: fedPoint?.date
                    });
                });
            });
        setFedPoints(pointsArray);
    }, [currentCalculator]);

    const typeOptions = [
        {
            value: 'field',
            label: t('method select field')
        },
        {
            value: 'zones',
            label: t('method select zone')
        },
        {
            value: 'points',
            label: t('method select point')
        }
    ];

    const zoneOptions = ['high', 'middle', 'low'].map(zone => ({
        value: zone,
        label: t(`zone select ${zone}`)
    }));

    const dateOptions = () => {
        const resultArray = [];

        fedPoints.forEach(fedPoint => {
            if (fedPoint?.date && !resultArray.includes(fedPoint.date)) {
                resultArray.push(fedPoint.date);
            }
        });

        return resultArray.map(date => ({
            value: date,
            label: moment(date).format('DD.MM.YYYY')
        }));
    };

    const pointOptions = () => {
        const resultArray = [];

        fedPoints.forEach(fedPoint => {
            const isSameDate = !selectedDate || fedPoint?.date === selectedDate;
            if (isSameDate && !resultArray.includes(fedPoint?.point_number)) {
                resultArray.push(fedPoint?.point_number);
            }
        });

        return resultArray.map(pointNumber => ({
            value: pointNumber,
            label: pointNumber
        }));
    };

    const onTypeChange = (newType) => {
        setSelectedType(newType);
        setSelectedZone([]);
        setSelectedPoint([]);
    };

    const onDateChange = (newDate) => {
        setSelectedDate(newDate);
        setSelectedPoint([]);
    };

    const isCalculateDisabled = !selectedDate
        || !selectedType
        || (selectedType === 'zones' && !selectedZone?.length)
        || (selectedType === 'points' && !selectedPoint?.length);

    const handleCalculate = () => {
        const requestBody = {
            type: selectedType,
            date: selectedDate
        };

        if (selectedType === 'zones') {
            requestBody.zones = selectedZone;
        }
        if (selectedType === 'points') {
            requestBody.points = selectedPoint;
        }

        dispatch(calculateDiagnostics({
            vega_key: match.params.id,
            body: requestBody
        }));

        setTimeout(() => {
            const { id, year } = match.params;
            if (id && year) {
                dispatch(getCalculatorMordovia({ id, year }));
            }
        }, 1500);
    };

    const deleteCalcNumber = (record) => {
        dispatch(removeCalculate(record?.id));
        
        const calcYear = currentCalculator?.year_group?.years?.filter(yearObj => yearObj?.year?.toString() === match.params.year?.toString());

        if (calcYear[0]) {
            const calcList = calcYear[0]?.calculator_group?.calculators?.filter(calc => calc?.calculator_type === 12 && calc?.id !== record?.id);
            setCalculatorsList(calcList);
        }

        setRequestLock(false);
    };

    const [requestLock, setRequestLock] = useState(false);

    const columns = [
        {
            title: t('table header type'),
            key: 'type',
            dataIndex: 'type',
            render: val => t(`table type ${val}`)
        },
        {
            title: t('table header date'),
            key: 'date',
            dataIndex: 'date',
            render: val => val ? moment(val).format('DD.MM.YYYY') : '-'
        },
        {
            title: t('table header points'),
            key: 'points',
            dataIndex: 'points'
        },
        {
            title: t('table header zones'),
            key: 'zones',
            dataIndex: 'zones',
            render: val => typeof val === 'string' ? t(`zone select ${val}`) : val?.length > 0 ? val?.map(x => t(`zone select ${x}`))?.join(', ') : ''
        },
        ...elementsArray?.map(elmnt => {
            return {
                title: t(`table element ${elmnt}`),
                key: elmnt,
                children: [
                    {
                        title: t('before lighting'),
                        key: ['fed', `c_${elmnt}`],
                        dataIndex: ['fed', `c_${elmnt}`]
                    },
                    {
                        title: t('after lighting'),
                        key: ['fed', `d_${elmnt}`],
                        dataIndex: ['fed', `d_${elmnt}`]
                    }
                ]
            }
        }),
        {
            key: 'actions',
            render: value => (
                <DeleteOutlined
                    onClick={() => {
                        setRequestLock(true);
                        deleteCalcNumber(value);
                    }} />
            )
        }
    ];

    const openCurrentCalc = (currCalc) => {
        if (currCalc?.fed?.fed_protocol) {
            dispatch(getProtocol({ id: currCalc?.fed?.fed_protocol }));
        } else {
            dispatch(clearProtocol());
        }
        setCurrentSelectedCalculator(currCalc?.fed);
    };

    const fertilizerColumns = [
        {
            title: tTable('name'),
            dataIndex: ['fertilizer', 'name'],
            key: 'name',
            // render: (text, record) => (
            //     <Tooltip
            //         placement="bottomLeft"
            //         title={text}>
            //         {text}
            //     </Tooltip>
            // )

        },
        {
            title: tTable('table elements header group'),
            children: elementsArray?.map(x => {
                return {
                    title: tTable(`table element ${x}`),
                    dataIndex: ['fertilizer', x?.toLowerCase()],
                    key: x?.toLowerCase(),
                    render: value => toFixed(value, 2)
                };
            })
        },
        {
            title: tTable('table fertilizer price group'),
            children: [
                {
                    title: tTable('table min'),
                    dataIndex: ['fertilizer', 'consumption_rate_min'],
                    key: 'consumption_rate_min'
                },
                {
                    title: tTable('table max'),
                    dataIndex: ['fertilizer', 'consumption_rate_max'],
                    key: 'consumption_rate_max'
                }
            ]
        },
        {
            title: tTable('avg_price'),
            dataIndex: ['price'],
            key: 'avg_price',
            sortType: 'number',
            render: (value, record) => record?.price ? value : record?.fertilizer?.price
        },
        {
            title: tTable('table consumption header group'),
            children: [
                {
                    title: tTable('table min'),
                    dataIndex: 'calc_cons_rate_min',
                    key: 'calc_cons_rate_min',
                    width: 100
                },
                {
                    title: tTable('table max'),
                    dataIndex: 'calc_cons_rate_max',
                    key: 'calc_cons_rate_max',
                    width: 100
                },
                {
                    title: tTable('table opt'),
                    dataIndex: 'calc_cons_rate_opt',
                    key: 'calc_cons_rate_opt',
                    width: 100
                }
            ]
        },
        {
            title: tTable('table price header group'),
            children: [
                {
                    title: tTable('table min'),
                    dataIndex: 'calc_price_min',
                    key: 'calc_price_min',
                    width: 100
                },
                {
                    title: tTable('table max'),
                    dataIndex: 'calc_price_max',
                    key: 'calc_price_max',
                    width: 100
                },
                {
                    title: tTable('table opt'),
                    dataIndex: 'calc_price_opt',
                    key: 'calc_price_opt',
                    width: 100
                }
            ]
        },
        //
        {
            title: tTable('table price area header group'),
            children: [
                {
                    title: tTable('table min'),
                    dataIndex: 'area_price_min',
                    key: 'area_price_min',
                    width: 100,
                    render: (value) => toFixed(value, 2)
                },
                {
                    title: tTable('table max'),
                    dataIndex: 'area_price_max',
                    key: 'area_price_max',
                    width: 100,
                    render: (value) => toFixed(value, 2)
                },
                {
                    title: tTable('table opt'),
                    dataIndex: 'area_price_opt',
                    key: 'area_price_opt',
                    width: 100,
                    render: (value) => toFixed(value, 2)
                }
            ]
        },
        {
            title: tTable('table norm area header group'),
            children: [
                {
                    title: tTable('table min'),
                    dataIndex: 'area_cons_rate_min',
                    key: 'area_cons_rate_min',
                    width: 100,
                    render: (value) => toFixed(value, 2)
                },
                {
                    title: tTable('table max'),
                    dataIndex: 'area_cons_rate_max',
                    key: 'area_cons_rate_max',
                    width: 100,
                    render: (value) => toFixed(value, 2)
                },
                {
                    title: tTable('table opt'),
                    dataIndex: 'area_cons_rate_opt',
                    key: 'area_cons_rate_opt',
                    width: 100,
                    render: (value) => toFixed(value, 2)
                }
            ]
        },
        {
            title: tTable('table rate'),
            dataIndex: 'rate',
            key: 'rate'
        },
    ];

    useEffect(() => {
        console.log('calculatorsList UPDATE', calculatorsList);
    }, [calculatorsList]);

    return (
        <div>
            <div className="leaf-interpretation__controls">
                <div className="leaf-interpretation__controls__item">
                    <div>{t('date label')}</div>
                    <Select
                        style={{ width: '100%' }}
                        value={selectedDate || undefined}
                        onChange={onDateChange}
                        options={dateOptions()}
                        placeholder={t('date placeholder')} />
                </div>

                <div className="leaf-interpretation__controls__item">
                    <div>{t('method label')}</div>
                    <Select
                        style={{ width: '100%' }}
                        value={selectedType || undefined}
                        onChange={onTypeChange}
                        options={typeOptions}
                        placeholder={t('method placeholder')} />
                </div>

                {selectedType === 'zones' && (
                    <div className="leaf-interpretation__controls__item">
                        <div>{t('zone label')}</div>
                        <Select
                            mode="multiple"
                            allowClear
                            style={{ width: '100%' }}
                            value={selectedZone}
                            onChange={(newZone) => setSelectedZone(newZone)}
                            options={zoneOptions}
                            placeholder={t('zone placeholder')} />
                    </div>
                )}

                {selectedType === 'points' && (
                    <div className="leaf-interpretation__controls__item">
                        <div>{t('point label')}</div>
                        <Select
                            mode="multiple"
                            allowClear
                            style={{ width: '100%' }}
                            value={selectedPoint}
                            onChange={(newPoint) => setSelectedPoint(newPoint)}
                            options={pointOptions()}
                            placeholder={t('point placeholder')} />
                    </div>
                )}

                <div className="leaf-interpretation__controls__item">
                    <Button
                        type="primary"
                        disabled={isCalculateDisabled}
                        onClick={handleCalculate}>
                        {t('calculate button')}
                    </Button>
                </div>
            </div>

            <Table
                rowClassName={(record, index) => record?.fed?.id === currentSelectedCalculator?.id ? 'selected-row' : ''}
                onRow={(record, rowIndex) => {
                    return {
                      onClick: event => {
                        if (requestLock) {
                            event?.preventDefault();
                        } else {
                            openCurrentCalc(record);
                        }
                      }
                    };
                }}
                rowKey="id"
                bordered
                columns={columns}
                dataSource={calculatorsList}
                scroll={{ x: 1500 }} />

            <Tabs defaultActiveKey="1">
                {calculatorsList?.map(calcItem => calcItem?.fed_fertilizers?.length > 0 ? (
                    <>
                        <TabPane
                            tab={`${calcItem?.type === 'field' ? `${t('field')}` : calcItem?.points?.length > 0 ? `${calcItem?.points?.length === 1 ? `${t('a point')} - ${calcItem?.points[0]}` : `${t('points')} - [${calcItem?.points?.join(', ')}]`}` : `${calcItem?.zones?.length === 1 ? `${t('zone')} - ${calcItem?.zones[0]}` : `${t('zones')} - ${calcItem?.zones?.map(x => t(`zone select ${x}`))?.join(', ')}`}`}`}
                            key={calcItem?.fed?.id}>
                            <LeafDiagnostics setCurrentCalc={setCurrentSelectedCalculator} currentCalc={calcItem?.fed} />
                        </TabPane>
                        <Row style={{ marginTop: '15px' }}>
                            <b>{t('recomm subhead')}</b>
                        </Row>
                        <Table bordered columns={fertilizerColumns} dataSource={currentFedProtocol?.fed_fertilizers} scroll={{ x: 1500 }} />
                    </>
                ) : (
                    <TabPane
                        tab={`${calcItem?.type === 'field' ? `${t('field')}` : calcItem?.points?.length > 0 ? `${calcItem?.points?.length === 1 ? `${t('a point')} - ${calcItem?.points[0]}` : `${t('points')} - [${calcItem?.points?.join(', ')}]`}` : `${calcItem?.zones?.length === 1 ? `${t('zone')} - ${calcItem?.zones[0]}` : `${t('zones')} - ${calcItem?.zones?.map(x => t(`zone select ${x}`))?.join(', ')}`}`}`}
                        key={calcItem?.fed?.id}>
                        <LeafDiagnostics setCurrentCalc={setCurrentSelectedCalculator} currentCalc={calcItem?.fed} />
                    </TabPane>
                ))}
            </Tabs>
        </div>
    );
};

export default LeafInterpetation;